import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  clearImpersonationHashFromLocation,
  readImpersonationPayloadFromLocation,
} from '../../utils/superAdminImpersonation';

/**
 * Consumes `#sa-impersonate=…` on tenant hosts and establishes a school-admin session
 * after a super-admin "Open as school admin" handoff.
 */
export const SuperAdminImpersonationHandoff: React.FC = () => {
  const { completeLoginFromResponse, isAuthenticated, loading } = useAuth();
  const history = useHistory();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (loading || isAuthenticated || busy) return;

    const payload = readImpersonationPayloadFromLocation();
    if (!payload) return;

    let cancelled = false;
    setBusy(true);
    setError(null);

    void (async () => {
      try {
        await completeLoginFromResponse(
          {
            session_token: payload.session_token,
            user_id: payload.user_id,
            tenant_id: payload.tenant_id,
            email: payload.email,
            permissions: payload.permissions ?? [],
            role: payload.role || 'admin',
          },
          payload.email
        );
        clearImpersonationHashFromLocation();
        if (!cancelled) {
          history.replace('/');
        }
      } catch (err) {
        clearImpersonationHashFromLocation();
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Could not open school admin session.');
          setBusy(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [busy, completeLoginFromResponse, history, isAuthenticated, loading]);

  if (!busy && !error) return null;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/40 px-4">
      <div className="max-w-md rounded-xl bg-white p-6 shadow-xl">
        <h2 className="text-lg font-semibold text-gray-900">
          {error ? 'Impersonation failed' : 'Opening school admin…'}
        </h2>
        <p className="mt-2 text-sm text-gray-600">
          {error || 'Signing you in as the tenant owner. This tab will become the school console.'}
        </p>
        {error ? (
          <button
            type="button"
            className="mt-4 rounded-md bg-primary-600 px-3 py-2 text-sm font-medium text-white hover:bg-primary-700"
            onClick={() => setError(null)}
          >
            Dismiss
          </button>
        ) : null}
      </div>
    </div>
  );
};
